// iot-admin/client/src/pages/GGOSDeviceDetail/GGOSDeviceDetail.js
// GGOS Device Detail page — left-nav + single-section content panel.

import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ggosDeviceService } from '../../services/api';
import ConnectionStatusBadge from '../../components/ConnectionStatusBadge';
import { DeactivateDeviceModal, ReactivateDeviceModal, ViewCertificateModal } from '../../components/GGOSDeviceModals';
import ActionsDropdown from '../../components/ActionsDropdown';
import {
  ArrowLeft,
  Monitor,
  PanelLeftClose,
  PanelLeft,
  Clipboard,
  ShieldCheck,
  Pause,
  Play,
  Building2,
  Package,
  Car, UserRound,
} from 'lucide-react';
import { getGroupedSections, getSectionById, DEFAULT_SECTION } from './sectionRegistry';
import './GGOSDeviceDetail.css';

const GGOSDeviceDetail = () => {
  const { thingName } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Device state ──
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // ── Modals ──
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);

  // ── Nav ──
  const [navCollapsed, setNavCollapsed] = useState(false);
  const activeSectionId = searchParams.get('section') || DEFAULT_SECTION;

  // ── Data fetching ──
  const fetchDevice = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ggosDeviceService.getByThingName(thingName);
      setDevice(response.data);
    } catch (err) {
      console.error('Error fetching device:', err);
      setError('Failed to load device details');
    } finally {
      setLoading(false);
    }
  }, [thingName]);

  useEffect(() => { fetchDevice(); }, [fetchDevice]);

  // Auto-refresh device header data every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const response = await ggosDeviceService.getByThingName(thingName);
        setDevice(response.data);
      } catch (_) { /* silent */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [thingName]);

  // Clear messages after 5s
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => { setMessage(null); setError(null); }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // ── Helpers ──
  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setMessage('Copied to clipboard');
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';
    const diffMs = Date.now() - new Date(dateString).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffMs / 86400000)}d ago`;
  };

  const setActiveSection = (id) => {
    setSearchParams({ section: id }, { replace: true });
  };

  // ── Loading / Error ──
  if (loading) {
    return (
      <div className="dd-loading">
        <div className="dd-spinner" />
        <span>Loading device...</span>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="page">
        <div className="alert alert-error">{error || 'Device not found'}</div>
        <button onClick={() => navigate('/ggos-devices')} className="btn btn-secondary">
          <ArrowLeft size={16} /> Back to GGOS Devices
        </button>
      </div>
    );
  }

  // ── Actions dropdown ──
  const actions = [
    { label: 'Copy Thing Name', icon: <Clipboard size={14} />, onClick: () => copyToClipboard(device.thingName) },
    { label: 'View Certificate', icon: <ShieldCheck size={14} />, onClick: () => setShowCertificate(true) },
    { divider: true },
    device.isActive
      ? { label: 'Deactivate Device', icon: <Pause size={14} />, danger: true, onClick: () => setShowDeactivate(true) }
      : { label: 'Reactivate Device', icon: <Play size={14} />, onClick: () => setShowReactivate(true) },
  ];

  // ── Resolve active section ──
  const activeSection = getSectionById(activeSectionId);
  const ActiveComponent = activeSection?.component;
  const groupedSections = getGroupedSections();

  return (
    <div className="dd-device-page">
      {/* ── Sticky Header ── */}
      <header className="dd-header">
        <div className="dd-header-left">
          <button onClick={() => navigate('/ggos-devices')} className="dd-back-btn" title="Back to GGOS Devices">
            <ArrowLeft size={18} />
          </button>
          <Monitor size={20} className="dd-device-icon" />
          <div className="dd-header-titles">
            <h1 className="dd-device-name">{device.thingName}</h1>
            <div className="dd-header-sub">
              <span className={`dd-sub-item ${device.inventory?.deviceName ? '' : 'dd-sub-item--muted'}`} title="Inventory device name">
                <Package size={12} /> {device.inventory?.deviceName || 'Unmatched — no inventory'}
              </span>
              {/* V250: the car this hardware sits in, as its own fact. The
                  placement name beside it is NOT the registration — it only
                  looked like one on the cabs somebody had named that way. */}
              {device.inventory?.vehicleRegistration && (
                <Link to={`/vehicles/${device.inventory.vehicleId}`} className="dd-sub-item" title="Vehicle">
                  <Car size={12} /> {device.inventory.vehicleRegistration}
                </Link>
              )}
              {device.inventory?.driverName && (
                device.inventory.driverId ? (
                  <Link to={`/drivers/${device.inventory.driverId}`} className="dd-sub-item" title="Active driver">
                    <UserRound size={12} /> {device.inventory.driverName}
                  </Link>
                ) : (
                  <span className="dd-sub-item" title="Active driver (assignment-time name; no linked driver record)">
                    <UserRound size={12} /> {device.inventory.driverName}
                  </span>
                )
              )}
              <span className={`dd-sub-item ${device.inventory?.tenantName ? '' : 'dd-sub-item--muted'}`} title="Tenant">
                <Building2 size={12} /> {device.inventory?.tenantName || 'No tenant'}
              </span>
            </div>
          </div>
          <ConnectionStatusBadge
            status={device.connectionStatus}
            lastConnectedAt={device.lastConnectedAt}
            autoRefresh
            size="small"
          />
          {device.lastConnectedAt && (
            <span className="dd-last-seen">
              {formatRelativeTime(device.lastConnectedAt)}
            </span>
          )}

        </div>
        <div className="dd-header-right">
          <ActionsDropdown actions={actions} />
        </div>
      </header>

      {message && <div className="alert alert-success dd-alert">{message}</div>}

      {/* ── Body: Nav + Content ── */}
      <div className="dd-body">
        {/* Left Nav */}
        <nav className={`dd-nav ${navCollapsed ? 'collapsed' : ''}`}>
          <button
            className="dd-nav-toggle"
            onClick={() => setNavCollapsed((c) => !c)}
            title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {navCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>

          <div className="dd-nav-groups">
            {groupedSections.map(({ group, items }) => (
              <div key={group} className="dd-nav-group">
                {!navCollapsed && <div className="dd-nav-group-label">{group}</div>}
                {items.map((section) => {
                  const Icon = section.icon;
                  const isActive = section.id === activeSectionId;
                  return (
                    <button
                      key={section.id}
                      className={`dd-nav-item ${isActive ? 'active' : ''}`}
                      onClick={() => setActiveSection(section.id)}
                      title={navCollapsed ? section.label : undefined}
                    >
                      <Icon size={18} />
                      {!navCollapsed && <span>{section.label}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </nav>

        {/* Content Panel */}
        <main className="dd-content">
          <Suspense
            fallback={
              <div className="dd-section-loading">
                <div className="dd-spinner" />
                <span>Loading section...</span>
              </div>
            }
          >
            {ActiveComponent ? (
              <ActiveComponent
                device={device}
                thingName={device.thingName}
                deviceStatus={device.connectionStatus}
                onMessage={setMessage}
                onRefreshDevice={fetchDevice}
              />
            ) : (
              <div className="dd-section-empty">
                <p>Section not found. Select a section from the left.</p>
              </div>
            )}
          </Suspense>
        </main>
      </div>

      {/* ── Modals ── */}
      <DeactivateDeviceModal
        isOpen={showDeactivate}
        onClose={() => setShowDeactivate(false)}
        device={device}
        onSuccess={() => { setMessage('Device deactivated'); fetchDevice(); }}
      />
      <ReactivateDeviceModal
        isOpen={showReactivate}
        onClose={() => setShowReactivate(false)}
        device={device}
        onSuccess={() => { setMessage('Device reactivated'); fetchDevice(); }}
      />
      <ViewCertificateModal
        isOpen={showCertificate}
        onClose={() => setShowCertificate(false)}
        device={device}
      />
    </div>
  );
};

export default GGOSDeviceDetail;
